/* istanbul ignore file */
// ut tests of use-folders are covered in CMUI level
/* eslint-disable arrow-body-style */
import Promise from 'bluebird';
import _ from 'underscore';
import { useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  ROOT_FOLDER_ID,
  RootFolderName,
  DEFAULT_PAGE_SIZE,
  CSMLSortBy,
  FolderAssetType,
} from '../../model';
import { civilDateRangeToDebugString } from '../../utils';
import { useDataServiceReducer } from './folder-reducer';
import { AdsStudioErrorBase, deleteFolderApiErrorProcessor } from '../../error-handling';

const PATH_SEPERATOR = ' > ';

const getPathString = (i18n, folderPath) => {
  // remove ROOT section from the string
  let pathString = folderPath.slice(1).map(fp => fp.folderName).join(PATH_SEPERATOR) || '';
  if (pathString) {
    pathString = `${PATH_SEPERATOR}${pathString}`;
  }
  pathString = `${i18n.getString(_TL_('Library'))}${pathString}`;
  return pathString;
};

export const getAssetTypeToFetch = ({ filterSettings, defaultAssetType }) => {
  const { assetTypeFilter } = filterSettings || {};

  return assetTypeFilter || defaultAssetType;
};

export const useFolders = ({
  perfMetricDateRange,
  defaultPerfMetricDateRange,
  repository,
  logger,
  assetType: defaultAssetType,
  i18n,
}) => {
  const ROOT_FOLDER = {
    folderId: ROOT_FOLDER_ID,
    folderName: i18n.getString(RootFolderName[defaultAssetType] || _TL_('Library')),
  };

  const {
    folderData, setFolderData, setAssetsOnly: setAssets, appendFolderData, setPerfTimeSeriesData, resetFolderData,
  } = useDataServiceReducer(logger);

  const [newFolderId, setNewFolderId] = useState('');
  const totalItemCount = useRef({});

  // set current folder navigation path
  // ROOT_FOLDER will always be element 0 in the paths folder state
  // when navigating, folders get pushed onto path
  // so top most folder is the current folder
  const [folderPath, setFolderPath] = useState([ROOT_FOLDER]);
  const currentFolder = useMemo(() => folderPath[folderPath.length - 1], [folderPath]);
  const currentFolderId = useMemo(() => currentFolder.folderId, [currentFolder]);

  const latestRequestTimestamp = useRef(0);

  /*
    searchTerm,
    minWidth,
    minHeight,
    dateAddedStart,
    dateAddedEnd,
    importSource,
    assetSources,
    assetTypeFilter,
    minVideoDuration,
    maxVideoDuration,
    isGlobalSearch,
    hasPerfData,
    topRated,
  */
  const curFilterSettings = useRef(null);

  const curSortBy = useRef(CSMLSortBy.MostRecent);
  const curAssetCountToLoadForSubFolders = useRef(1);
  const curFolderAssetTypeToLoad = useRef(defaultAssetType);
  const curIncludeParentHierarchy = useRef(false);
  const curIncludeSubfolders = useRef(true);
  const curPageName = useRef(undefined);
  const curPerfMarker = useRef(undefined);

  const pendingVideoBlobUploadPromiseTokens = useRef([]);

  const resetData = useCallback(() => {
    resetFolderData();
    curFilterSettings.current = null;
    curSortBy.current = CSMLSortBy.MostRecent;
    curAssetCountToLoadForSubFolders.current = 1;
    curFolderAssetTypeToLoad.current = defaultAssetType;
    curIncludeParentHierarchy.current = false;
    curIncludeSubfolders.current = true;
    curPageName.current = undefined;
    curPerfMarker.current = undefined;
  }, [defaultAssetType, resetFolderData]);

  const logTrace = useCallback((message, ...rest) => {
    logger.logTrace(`DataService: ${message}`, ...rest);
  }, [logger]);

  const loadFolderArgs = useCallback(({
    filterSettings,
    sortBy,
    assetsToLoadForSubfolders,
    includeParentHierarchy,
    count,
  }) => {
    const folderAssetTypeToFetch = getAssetTypeToFetch({
      filterSettings,
      defaultAssetType,
    });

    return repository.getFolderArgs({
      perfMetricDateRange,
      ...filterSettings,
      sortBy,
      skip: 0,
      count: count || DEFAULT_PAGE_SIZE,
      folderId: currentFolderId,
      assetType: folderAssetTypeToFetch,
      includeAssets: true,
      includeSubfolders: true,
      includeParentHierarchy,
      assetsToLoadForSubfolders,
      isAssetPickerScenario: defaultAssetType !== null,
    });
  }, [defaultAssetType, repository, perfMetricDateRange, currentFolderId]);

  const loadFolderDataInternal = useCallback(({
    hasDateRange,
    filterSettings,
    sortBy,
    folderId,
    assetsToLoadForSubfolders,
    includeParentHierarchy,
    count,
    includeSubfolders = true,
    pageName,
    perfMarker,
    withBIData = false,
  }) => {
    const requestTimestamp = Date.now();
    const folderAssetTypeToFetch = getAssetTypeToFetch({
      filterSettings,
      defaultAssetType,
    });
    const saveCurrentFilters = () => {
      curSortBy.current = sortBy;
      curFilterSettings.current = filterSettings;
      curFolderAssetTypeToLoad.current = folderAssetTypeToFetch;
      curAssetCountToLoadForSubFolders.current = assetsToLoadForSubfolders;
      curIncludeParentHierarchy.current = includeParentHierarchy;
      curIncludeSubfolders.current = includeSubfolders;
      curPageName.current = pageName;
      curPerfMarker.current = perfMarker;
    };
    return repository.getFolderData({
      perfMetricDateRange: hasDateRange ? perfMetricDateRange : defaultPerfMetricDateRange,
      ...filterSettings,
      sortBy,
      skip: 0,
      count: count || DEFAULT_PAGE_SIZE,
      folderId,
      assetType: folderAssetTypeToFetch,
      includeAssets: true,
      includeSubfolders,
      includeParentHierarchy,
      assetsToLoadForSubfolders,
      isAssetPickerScenario: defaultAssetType !== null,
      withBIData,
      perfMarker,
    }).then((res) => {
      if (requestTimestamp >= latestRequestTimestamp.current) {
        const { folder } = res;
        const newFolders = folder.subfolders || [];
        const newAssets = folder.assets || [];
        const perfTimeSeriesData = folder.perfTimeSeriesData || [];

        totalItemCount.current = folder.itemCount;
        saveCurrentFilters();

        setFolderData({ newAssets, newFolders, newPerfTimeSeries: perfTimeSeriesData }, pageName);

        latestRequestTimestamp.current = requestTimestamp;

        const {
          searchTerm: searchText, dateAddedStart, dateAddedEnd, ...otherFilters
        } = filterSettings || {};
        logTrace('FolderDataService: loaded assets, folders', {
          ...otherFilters,
          searchText,
          dateAddedDateRange: (dateAddedStart != null && dateAddedEnd != null) ? civilDateRangeToDebugString({ start: dateAddedStart, end: dateAddedEnd }) : undefined,
          sortBy: curSortBy.current,
          skip: 0,
          count: DEFAULT_PAGE_SIZE,
          assetType: curFolderAssetTypeToLoad.current,
          hasDateRange,
        });
      } else {
        logTrace('DataService: load folders and assets through folder service, ignore other requests');
      }

      return res;
    }).tapCatch(() => {
      if (requestTimestamp > latestRequestTimestamp.current) {
        saveCurrentFilters();
        latestRequestTimestamp.current = requestTimestamp;
      }
    });
  }, [defaultAssetType, repository, perfMetricDateRange, defaultPerfMetricDateRange, setFolderData, logTrace]);

  const loadFolderData = useCallback(({
    filterSettings,
    sortBy,
    assetsToLoadForSubfolders,
    includeParentHierarchy,
    count,
    includeSubfolders = true,
    pageName,
    perfMarker,
  }) => {
    return loadFolderDataInternal({
      hasDateRange: false,
      filterSettings,
      sortBy,
      folderId: currentFolderId,
      assetsToLoadForSubfolders,
      includeParentHierarchy,
      count,
      includeSubfolders,
      pageName,
      perfMarker,
    });
  }, [loadFolderDataInternal, currentFolderId]);

  const loadFolderDataInDateRange = useCallback(({
    filterSettings,
    sortBy,
    assetsToLoadForSubfolders,
    includeParentHierarchy,
    count,
    includeSubfolders = true,
    pageName,
    perfMarker,
  }) => loadFolderDataInternal({
    hasDateRange: true,
    filterSettings,
    sortBy,
    folderId: currentFolderId,
    assetsToLoadForSubfolders,
    includeParentHierarchy,
    count,
    includeSubfolders,
    pageName,
    perfMarker,
    withBIData: true,
  }), [loadFolderDataInternal, currentFolderId]);

  const loadPerfTimeSeriesData = useCallback(({ perfTimeSeriesParams, filterSettings }) => {
    const ret = repository.getPerfTimeSeriesData({
      perfMetricDateRange,
      perfTimeSeriesParams,
      ...(filterSettings || {}),
      folderId: currentFolderId === ROOT_FOLDER_ID ? -1 : currentFolderId,
    }).then((res) => {
      const { perfTimeSeriesData } = res || {};
      setPerfTimeSeriesData({ newPerfTimeSeries: perfTimeSeriesData });
      logTrace('FolderDataService: loaded performance timeseries data');
      return perfTimeSeriesData;
    });
    return ret;
  }, [currentFolderId, logTrace, perfMetricDateRange, repository, setPerfTimeSeriesData]);

  const loadMoreFolderDataImpl = useCallback(({ hasDateRange, count = DEFAULT_PAGE_SIZE, withBIData = false }) => {
    const requestTimestamp = Date.now();

    const { folders, assets } = folderData;

    const skip = (folders ? folders.length : 0) + (assets ? assets.length : 0);

    // avoid error: Invalid $top, $skip Combination. $skip should be multiple of $top
    // this could happen if additional loadmore request (maybe caused by grid rerender) is sent after all items are aready loaded
    if (totalItemCount.current && skip >= totalItemCount.current.TotalCount) {
      logTrace('skip this request as all items are already loaded.', {
        skip, top: count, totalItemCount: totalItemCount.current.TotalCount, hasDateRange,
      });
      return Promise.resolve({});
    }
    if (count && skip % count) {
      logTrace('$skip is not multiple of $top', {
        skip, top: count, totalItemCount: totalItemCount.current.TotalCount, hasDateRange,
      });
      return Promise.resolve({});
    }

    return repository.getFolderData({
      perfMetricDateRange: hasDateRange ? perfMetricDateRange : defaultPerfMetricDateRange,
      ...curFilterSettings.current,
      sortBy: curSortBy.current,
      skip,
      folderId: currentFolderId,
      count: count || DEFAULT_PAGE_SIZE,
      assetType: curFolderAssetTypeToLoad.current,
      includeAssets: true,
      includeSubfolders: curIncludeSubfolders.current,
      includeParentHierarchy: curIncludeParentHierarchy.current,
      assetsToLoadForSubfolders: curAssetCountToLoadForSubFolders.current,
      isAssetPickerScenario: defaultAssetType !== null,
      withBIData,
      perfMarker: curPerfMarker.current,
    }).then((res) => {
      if (requestTimestamp >= latestRequestTimestamp.current) {
        const { folder } = res;
        const newFolders = folder.subfolders || [];
        const newAssets = folder.assets || [];

        latestRequestTimestamp.current = requestTimestamp;

        logTrace('DataService: loaded more folders and assets through folder service', {
          sortBy: curSortBy.current,
          skip,
          count,
          filterSettings: curFilterSettings.current,
          assetType: curFolderAssetTypeToLoad.current,
          hasDateRange,
        });

        appendFolderData({ newFolders, newAssets });
      } else {
        logTrace('DataService: load more data, ignore other requests');
      }

      return res;
    }).tapCatch(() => {
      logger.logError('Failed to load more data');
    });
  }, [folderData, repository, perfMetricDateRange, defaultPerfMetricDateRange, currentFolderId, defaultAssetType, logTrace, appendFolderData, logger]);

  const loadMoreFolderData = useCallback(({ count = DEFAULT_PAGE_SIZE }) => loadMoreFolderDataImpl({ hasDateRange: false, count }), [loadMoreFolderDataImpl]);
  const loadMoreFolderDataInDateRange = useCallback(({ count = DEFAULT_PAGE_SIZE }) => loadMoreFolderDataImpl({ hasDateRange: true, count, withBIData: true }), [loadMoreFolderDataImpl]);

  const reloadFolders = useCallback(({ count } = {}) => loadFolderDataInternal({
    hasDateRange: false,
    filterSettings: curFilterSettings.current,
    folderId: currentFolderId,
    sortBy: curSortBy.current,
    assetsToLoadForSubfolders: curAssetCountToLoadForSubFolders.current,
    includeParentHierarchy: curIncludeParentHierarchy.current,
    includeSubfolders: curIncludeSubfolders.current,
    count,
    pageName: curPageName.current,
    perfMarker: curPerfMarker.current,
  }), [loadFolderDataInternal, currentFolderId]);

  const reloadFoldersInDateRange = useCallback(({ count } = {}) => loadFolderDataInternal({
    hasDateRange: true,
    filterSettings: curFilterSettings.current,
    folderId: currentFolderId,
    sortBy: curSortBy.current,
    assetsToLoadForSubfolders: curAssetCountToLoadForSubFolders.current,
    includeParentHierarchy: curIncludeParentHierarchy.current,
    includeSubfolders: curIncludeSubfolders.current,
    count,
    pageName: curPageName.current,
    perfMarker: curPerfMarker.current,
  }), [loadFolderDataInternal, currentFolderId]);

  // use method to navigate into a folder that's a child of the current folder
  // i.e. user clicking a folder that's displayed in the folder grid
  const navigateIntoFolder = useCallback(({ id = '', navigateToNewFolder = false }) => {
    let folderToNavigate;

    const { folders } = folderData;

    if (navigateToNewFolder && !id && newFolderId) {
      folderToNavigate = _.find(folders, ({ folderId }) => folderId === newFolderId);
    } else if (id) {
      folderToNavigate = _.find(folders, ({ folderId }) => folderId === id);
    }

    const { parentHierarchy } = folderToNavigate;
    if (parentHierarchy && parentHierarchy.length > 0) {
      setFolderPath([ROOT_FOLDER, ...parentHierarchy]);
    } else {
      setFolderPath(curFolderPath => [
        ...curFolderPath,
        folderToNavigate,
      ]);
    }
    setNewFolderId('');
  }, [ROOT_FOLDER, folderData, newFolderId]);

  // use method to navigate to a folder in current path
  // i.e. user clicking a parent folder that's displayed in the summary bar
  const navigateToFolderInPath = useCallback(({ id }) => {
    setFolderPath((curFolderPath) => {
      const indexOfFolderInPath = _.findIndex(curFolderPath, ({ folderId }) => folderId === id);
      return curFolderPath.slice(0, indexOfFolderInPath + 1);
    });
    setNewFolderId('');
  }, [setFolderPath]);

  // use method to navigate into a folder in navigation tree
  // i.e. user clicking a folder in the navigation tree or in recent of home page
  const navigateIntoFolderItem = useCallback(({ folder }) => {
    const { parentHierarchy = [] } = folder;
    setFolderPath([ROOT_FOLDER, ...parentHierarchy]);
  }, [ROOT_FOLDER]);

  const navigateIntoRootFolder = useCallback(() => {
    setFolderPath([ROOT_FOLDER]);
  }, [ROOT_FOLDER]);

  const createFolder = useCallback(({ folderName }) => {
    return repository.createFolder({ folderName, parentFolderId: currentFolderId });
  }, [repository, currentFolderId]);

  const createFolderBulk = useCallback(({ folderName, parentFolderId }) => {
    return repository.createFolder({ folderName, parentFolderId });
  }, [repository]);

  const deleteEmptyFolder = useCallback(({ folderId, folderName }) => {
    return repository.deleteFolder({ folderId, folderName });
  }, [repository]);

  const renameFolder = useCallback(({ folderId, newFolderName }) => {
    return repository.renameFolder({ folderId, newFolderName });
  }, [repository]);

  const uploadImageByUrlToFolder = useCallback(({
    imageUrl,
    folderId,
    sourceType,
  }) => {
    return repository.addUrlImageToFolder({
      imageSrc: imageUrl,
      folderId: folderId || currentFolderId,
      sourceType,
    });
  }, [repository, currentFolderId]);

  const addImageWithBaseIdToFolder = useCallback(({
    folderId,
    imageSrc,
    metaData,
    baseId,
    sourceType,
  }) => {
    return repository.addImageWithBaseIdToFolder({
      folderId: folderId || currentFolderId,
      imageSrc,
      metaData,
      baseId,
      sourceType,
    });
  }, [currentFolderId, repository]);

  const uploadVideoToAzureBlob = useCallback(({
    fileObject,
    onProgress,
  }) => {
    const promise = new Promise((resolve, reject) => {
      repository.uploadVideoToAzureBlobWithCancellation({
        fileObject,
        onProgress,
      }).then(({ token, blobName }) => {
        pendingVideoBlobUploadPromiseTokens.current.push(token);

        token.on({
          onSuccess: () => resolve({ token, blobName }),
          onError: reject,
          onCancel: () => {
            logTrace('DataService: cancel azure upload for video file');

            reject();
          },
        });
      });
    });

    return promise;
  }, [logTrace, repository]);

  const uploadVideoToFolder = useCallback(({
    fileObject,
    updateCurrentProgress,
    name,
    folderId,
    perfMarker,
  }) => {
    let uploadVideoPerfMarker = null;
    if (perfMarker) {
      uploadVideoPerfMarker = perfMarker.createChild('uploadSingleVideo');
      uploadVideoPerfMarker.willInit();
    }
    // blob upload stage: 0.9 progress
    // save blob to MT: 0.1 progress
    const scale = 0.9;
    const progressForBlobUpload = updateCurrentProgress ? (percentage) => {
      return updateCurrentProgress({
        loaded: percentage * scale,
        total: 1,
      });
    } : undefined;
    const progressForSavingBlobToMT = updateCurrentProgress ? (evt) => {
      const { loaded, total } = evt;

      return updateCurrentProgress({
        loaded: (total * scale) + (loaded * (1 - scale)),
        total,
      });
    } : undefined;

    return uploadVideoToAzureBlob({
      fileObject,
      onProgress: progressForBlobUpload,
    }).then(({ token, blobName }) => {
      pendingVideoBlobUploadPromiseTokens.current = _.without(pendingVideoBlobUploadPromiseTokens.current, token);

      return repository.addFolderUploadAsset({
        folderId: folderId || currentFolderId,
        asset: { blobName, name },
        folderAssetType: FolderAssetType.Video,
        inProgress: progressForSavingBlobToMT,
      }).finally(() => {
        if (uploadVideoPerfMarker) {
          uploadVideoPerfMarker.done();
        }
      });
    });
  }, [currentFolderId, repository, uploadVideoToAzureBlob]);

  const cancelPendingVideoUpload = () => {
    _.each(pendingVideoBlobUploadPromiseTokens.current, (token) => {
      token.cancel();
    });
  };

  const postVideoByUrl = useCallback(({
    videoUrl,
    name,
    folderId,
  }) => {
    return repository.postVideoByUrl({
      folderId: folderId || currentFolderId,
      videoUrl,
      name,
    });
  }, [currentFolderId, repository]);

  const uploadImageToFolder = useCallback(({
    imageData,
    updateCurrentProgress,
    imageName,
    folderId,
    supportUpscale,
  }) => {
    return repository.addFolderUploadAsset({
      folderId: folderId || currentFolderId,
      asset: { data: imageData, name: imageName },
      folderAssetType: FolderAssetType.Image,
      inProgress: updateCurrentProgress,
      supportUpscale,
    });
  }, [currentFolderId, repository]);

  const uploadStockImageToFolder = useCallback(({
    stockImageId,
    folderId,
  }) => {
    return repository.addStockImageToFolder({
      folderId: folderId || currentFolderId,
      stockImageId,
    });
  }, [repository, currentFolderId]);

  const uploadInvestImageToFolder = useCallback(({
    investImageId,
    imageSrc,
    folderId,
  }) => {
    return repository.addInvestImageToFolder({
      folderId: folderId || currentFolderId,
      investImageId,
      imageSrc,
    });
  }, [repository, currentFolderId]);

  const uploadInvestVideoToFolder = useCallback(({
    investVideoId,
    videoSrc,
    description,
    folderId,
  }) => {
    return repository.addInvestVideoToFolder({
      folderId: folderId || currentFolderId,
      investVideoId,
      videoSrc,
      description,
    });
  }, [repository, currentFolderId]);

  const renameAsset = useCallback(({
    assetId,
    folderAssetType,
    name,
  }) => {
    return repository.renameAsset({
      assetId,
      folderAssetType,
      name,
    }).then(() => {
      logTrace('DataService: asset is renamed', { assetId, folderAssetType, newName: name });

      // update the internal state
      const { assets } = folderData;
      const idx = _.findIndex(assets, asset => asset.id === assetId);
      if (idx >= 0 && assets[idx].name !== name) {
        const newAsset = { ...assets[idx], name };
        const newAssets = [...assets];
        newAssets.splice(idx, 1, newAsset);
        setAssets(newAssets);
      }
    });
  }, [folderData, logTrace, repository, setAssets]);

  const deleteAsset = useCallback(({
    assetId,
    folderAssetType,
  }) => {
    return repository.deleteAsset({ assetId, folderAssetType })
      .tap(() => {
        logTrace('DataService: asset is deleted', { assetId, folderAssetType });
      });
  }, [repository, logTrace]);

  // eslint-disable-next-line no-underscore-dangle
  const _batchDeleteAssetsAsync = useCallback(({ assets, parentPath, onItemDeleted }) => {
    return repository.bulkDeleteAssets(assets).then(({ resultSummary }) => {
      const ret = _.map(resultSummary, ({
        err, id: assetId, name: assetName, folderAssetType, url, thumbnailUrl,
      }) => {
        onItemDeleted({
          err, assetId, assetName, path: parentPath, assetType: folderAssetType, thumbnailUrl: thumbnailUrl || url, api: 'bulkdeleteAssetApi', errorCode: err && err.errorCode,
        });
        return {
          err, assetId, assetName, parentPath, folderAssetType,
        };
      });
      return ret;
    }).catch((err) => {
      const ret = _.map(assets, ({
        id: assetId, name: assetName, folderAssetType, url, thumbnailUrl,
      }) => {
        onItemDeleted({
          err, assetId, assetName, path: parentPath, assetType: folderAssetType, thumbnailUrl: thumbnailUrl || url, api: 'bulkdeleteAssetApi', errorCode: err && err.errorCode,
        });
        return {
          err, assetId, assetName, parentPath, folderAssetType,
        };
      });
      return ret;
    });
  }, [repository]);

  // eslint-disable-next-line no-underscore-dangle
  const _deleteEmptyFolderAsync = useCallback(({
    folderId, folderName, parentPath, onItemDeleted = _.noop,
  }) => {
    return deleteEmptyFolder({ folderId, folderName }).then(() => {
      onItemDeleted({
        folderId, folderName, path: parentPath, api: 'deleteFolderApi',
      });
      return {
        folderId, folderName, parentPath,
      };
    }).catch((err) => {
      onItemDeleted({
        err, folderId, folderName, path: parentPath, api: 'deleteFolderApi', errorCode: err && err.errorCode,
      });
      return {
        err, folderId, folderName, parentPath,
      };
    });
  }, [deleteEmptyFolder]);

  // MT only support delete empty folder, to delete a folder with assets & subfolders,
  // need to delete its subfolders one by one, and batch delete its assets
  // eslint-disable-next-line no-underscore-dangle
  const _batchDelete = useCallback(({
    folders = [], assets = [], path, onItemDeleted, _deleteFolder,
  }) => {
    const parentPath = path || getPathString(i18n, folderPath);
    const concurrencyCount = 3;

    return Promise.map(
      folders,
      ({ folderId, folderName, itemCount: ic }) => _deleteFolder({
        folderId, folderName, path: parentPath, totalItemCount: ic.TotalCount, onItemDeleted,
      }),
      { concurrency: concurrencyCount }
    ).then((foldersResult) => {
      return _batchDeleteAssetsAsync({ assets, parentPath, onItemDeleted }).then((assetsResult) => {
        return { folders: foldersResult, assets: assetsResult };
      });
    });
  }, [_batchDeleteAssetsAsync, folderPath, i18n]);

  const deleteFolder = useCallback(({
    folderId,
    folderName,
    totalItemCount: totalItmCnt, // optional param
    path,
    onItemDeleted,
    skip = 0,
  }) => {
    const parentPath = path || getPathString(i18n, folderPath);

    // query one page (without any filters) and then delete returned items.
    const loadOnePageAndDeleteAsync = () => {
      const ret = repository.getFolderData({
        folderId, sortBy: curSortBy.current, skip, count: DEFAULT_PAGE_SIZE, includeAssets: true, includeSubfolders: true,
      }).then((res) => {
        const { folder } = res;
        const { subfolders, assets, itemCount: { TotalCount: totalCount } } = folder || {};

        logTrace('delete folder - loaded data to delete', { folderId, folderName, totalItemCount: totalCount });

        // folder is empty
        if (totalCount <= 0) {
          return { hasNextPage: false };
        }

        // folder is not empty, batch deleting
        return _batchDelete({
          folders: subfolders, assets, path: `${parentPath}${PATH_SEPERATOR}${folderName}`, onItemDeleted, _deleteFolder: deleteFolder,
        }).then((results) => {
          // stop if all tasks failed
          let newSkip = skip;
          const successCount = _.filter([...results.folders, ...results.assets], ({ err }) => !err).length;
          if (successCount <= 0) {
            // all failed, skip the page
            newSkip = skip + DEFAULT_PAGE_SIZE;
          }

          // all items are deleted (some of them may failed)
          return { hasNextPage: totalCount > skip + DEFAULT_PAGE_SIZE, newSkip };
        });
      }).catch((e) => {
        // failed to load data, stop handling this folder
        const err = new AdsStudioErrorBase({ message: 'failed to load data to delete', originError: e, errorProcessor: deleteFolderApiErrorProcessor });
        onItemDeleted({
          e, folderId, folderName, path: parentPath, api: 'getFolderDataApi', errorCode: err && err.errorCode,
        });


        return { hasNextPage: undefined, loadDataError: err };
      });
      return ret;
    };

    // current folder is empty
    if (totalItmCnt <= 0) {
      return _deleteEmptyFolderAsync({
        folderId, folderName, parentPath: path, onItemDeleted,
      });
    }

    // load subfolders and assets for 'folderId' page by page and then delete them one by one
    return loadOnePageAndDeleteAsync().then(({ hasNextPage, newSkip, loadDataError }) => {
      // continue to load next page and delete it
      if (hasNextPage === true) {
        return deleteFolder({
          folderId, folderName, totalItemCount: totalItmCnt, parentPath, onItemDeleted, skip: newSkip,
        });
      } else if (hasNextPage === false) {
        // try to delete the folder itself
        return _deleteEmptyFolderAsync({
          folderId, folderName, parentPath, onItemDeleted, skip: newSkip,
        });
      }

      // failed to load data (!!loadDataError), stop handling this folder
      return {
        folderId, folderName, parentPath, err: loadDataError,
      };
    // }).tap((result) => {
    //   console.log('####', result);
    });
  }, [_batchDelete, _deleteEmptyFolderAsync, folderPath, i18n, logTrace, repository]);

  const batchDelete = useCallback(({
    folders = [], assets = [], path, onItemDeleted = _.noop,
  }) => _batchDelete({
    folders, assets, path, onItemDeleted, _deleteFolder: deleteFolder,
  }), [_batchDelete, deleteFolder]);

  const batchMove = useCallback(({ destFolderId, folders = [], assets = [] }) => {
    return repository.batchMove({ destFolderId, folders, assets });
  }, [repository]);

  const folderContext = useMemo(() => ({
    folders: folderData.folders,
    folderPath,
    loadFolderData,
    loadMoreFolderData,
    loadFolderDataInDateRange,
    loadFolderArgs,
    loadMoreFolderDataInDateRange,
    perfTimeSeriesData: folderData.perfTimeSeriesData,
    loadPerfTimeSeriesData,
    navigateIntoFolder,
    navigateToFolderInPath,
    navigateIntoFolderItem,
    navigateIntoRootFolder,
    createFolder,
    deleteFolder,
    renameFolder,
    createFolderBulk,
    currentFolder,
    uploadImageByUrlToFolder,
    currentFolderId,
    uploadVideoToFolder,
    cancelPendingVideoUpload,
    postVideoByUrl,
    uploadImageToFolder,
    uploadStockImageToFolder,
    uploadInvestImageToFolder,
    uploadInvestVideoToFolder,
    reloadFolders,
    reloadFoldersInDateRange,
    totalItemCount,
    assets: folderData.assets,
    setAssets,
    renameAsset,
    deleteAsset,
    batchDelete,
    batchMove,
    addImageWithBaseIdToFolder,
    resetFolderData: resetData,
    dataOwnerPageName: folderData.pageName,
  }), [folderData.folders, folderData.perfTimeSeriesData, folderData.assets, folderData.pageName, folderPath, loadFolderData, loadMoreFolderData, loadFolderDataInDateRange, loadFolderArgs, loadMoreFolderDataInDateRange, loadPerfTimeSeriesData, navigateIntoFolder, navigateToFolderInPath, navigateIntoRootFolder, navigateIntoFolderItem, createFolder, deleteFolder, renameFolder, createFolderBulk, currentFolder, uploadImageByUrlToFolder, currentFolderId, uploadVideoToFolder, postVideoByUrl, uploadImageToFolder, uploadStockImageToFolder, uploadInvestImageToFolder, uploadInvestVideoToFolder, reloadFolders, reloadFoldersInDateRange, setAssets, renameAsset, deleteAsset, batchDelete, batchMove, resetData, addImageWithBaseIdToFolder]);

  return folderContext;
};

useFolders.propTypes = {
  repository: PropTypes.shape({
    getFolders: PropTypes.func.isRequired,
    getImages: PropTypes.func.isRequired,
    createFolder: PropTypes.func.isRequired,
  }).isRequired,
  logger: PropTypes.shape({
    logTrace: PropTypes.func.isRequired,
  }).isRequired,
};
